import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SafeStorage } from '../utils/safeStorage';

export type PatternIntensity = 'off' | 'subtle' | 'full';
export type ReadingMode = 'normal' | 'reading';

interface SettingsContextType {
  // Visual preferences
  patternIntensity: PatternIntensity;
  setPatternIntensity: (intensity: PatternIntensity) => void;

  readingMode: ReadingMode;
  setReadingMode: (mode: ReadingMode) => void;

  // Accessibility
  reducedMotion: boolean;
  highContrast: boolean;
  setHighContrast: (enabled: boolean) => void;

  // Privacy
  profileVisibility: 'public' | 'friends' | 'private';
  setProfileVisibility: (visibility: 'public' | 'friends' | 'private') => void;

  allowMessages: 'everyone' | 'friends' | 'none';
  setAllowMessages: (setting: 'everyone' | 'friends' | 'none') => void;

  shareLocation: boolean;
  setShareLocation: (enabled: boolean) => void;

  // Friend list visibility
  showFriendList: boolean;
  setShowFriendList: (show: boolean) => void;

  friendListVisibility: 'everyone' | 'friends' | 'private';
  setFriendListVisibility: (visibility: 'everyone' | 'friends' | 'private') => void;

  // Tribe list visibility
  showTribesList: boolean;
  setShowTribesList: (show: boolean) => void;

  tribeListVisibility: 'everyone' | 'friends' | 'private';
  setTribeListVisibility: (visibility: 'everyone' | 'friends' | 'private') => void;

  // Yap list visibility
  showYaps: boolean;
  setShowYaps: (show: boolean) => void;

  // Discover visibility
  showInDiscover: boolean;
  setShowInDiscover: (show: boolean) => void;

  // Notifications
  notificationPrefs: {
    likes: boolean;
    comments: boolean;
    shouts: boolean;
    messages: boolean;
    connections: boolean;
    tribeActivity: boolean;
    system: boolean;
    yapDay: boolean;
    dailyPrompt: boolean;
    legionPinnedYap: boolean;
  };
  setNotificationPrefs: (prefs: Partial<SettingsContextType['notificationPrefs']>) => void;

  /**
   * Sync privacy settings FROM Firestore profile data.
   * Call this from UserDataContext whenever the user profile snapshot arrives.
   * Firestore values are authoritative and override localStorage cache.
   */
  syncFromProfile: (profile: {
    showFriendList?: boolean;
    friendListVisibility?: 'everyone' | 'friends' | 'private';
    showTribesList?: boolean;
    tribeListVisibility?: 'everyone' | 'friends' | 'private';
    showYaps?: boolean;
    showInDiscover?: boolean;
    allowMessages?: 'everyone' | 'friends' | 'none';
  }) => void;

  // Get pattern CSS class based on current settings
  getPatternClass: () => string;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const DEFAULT_NOTIFS = {
  likes: true,
  comments: true,
  shouts: true,
  messages: true,
  connections: true,
  tribeActivity: true,
  system: true,
  yapDay: true,
  dailyPrompt: true,
  legionPinnedYap: true,
};

export function SettingsProvider({ children }: { children: ReactNode }) {
  // Load from localStorage or use defaults
  const [notificationPrefs, setNotificationPrefsState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_notif_prefs');
      return saved ? JSON.parse(saved) : DEFAULT_NOTIFS;
    }
    return DEFAULT_NOTIFS;
  });

  const setNotificationPrefs = (prefs: Partial<SettingsContextType['notificationPrefs']>) => {
    setNotificationPrefsState((prev: any) => {
      const next = { ...prev, ...prefs };
      SafeStorage.setItem('afroyap_notif_prefs', JSON.stringify(next));
      return next;
    });
  };
  const [patternIntensity, setPatternIntensityState] = useState<PatternIntensity>(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_pattern_intensity');
      return (saved as PatternIntensity) || 'full';
    }
    return 'full';
  });

  const [readingMode, setReadingModeState] = useState<ReadingMode>('normal');

  const [highContrast, setHighContrastState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_high_contrast');
      return saved === 'true';
    }
    return false;
  });

  const [profileVisibility, setProfileVisibilityState] = useState<'public' | 'friends' | 'private'>(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_profile_visibility');
      return (saved as 'public' | 'friends' | 'private') || 'public';
    }
    return 'public';
  });

  // Defaults to friends-only. It used to default to 'everyone', so every account
  // that never opened this setting was open to DMs from anyone -- opt-out rather
  // than opt-in for a safety control.
  const [allowMessages, setAllowMessagesState] = useState<'everyone' | 'friends' | 'none'>(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_allow_messages');
      return (saved as 'everyone' | 'friends' | 'none') || 'friends';
    }
    return 'friends';
  });

  const [shareLocation, setShareLocationState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_share_location');
      if (saved === null) return true;
      return saved === 'true';
    }
    return true;
  });

  // Default TRUE — will be authoritative-overridden by syncFromProfile() when Firestore arrives
  const [showFriendList, setShowFriendListState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_show_friend_list');
      if (saved === null) return true; // Default: show list (true)
      return saved === 'true';
    }
    return true;
  });

  const [friendListVisibility, setFriendListVisibilityState] = useState<'everyone' | 'friends' | 'private'>(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_friend_list_visibility');
      return (saved as 'everyone' | 'friends' | 'private') || 'everyone';
    }
    return 'everyone';
  });

  // Default TRUE — will be authoritative-overridden by syncFromProfile() when Firestore arrives
  const [showTribesList, setShowTribesListState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_show_tribes_list');
      if (saved === null) return true; // Default: show list (true)
      return saved === 'true';
    }
    return true;
  });

  const [tribeListVisibility, setTribeListVisibilityState] = useState<'everyone' | 'friends' | 'private'>(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_tribe_list_visibility');
      return (saved as 'everyone' | 'friends' | 'private') || 'everyone';
    }
    return 'everyone';
  });

  const [showYaps, setShowYapsState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_show_yaps');
      if (saved === null) return true; // Default: show yaps (true)
      return saved === 'true';
    }
    return true;
  });

  const [showInDiscover, setShowInDiscoverState] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = SafeStorage.getItem('afroyap_show_in_discover');
      if (saved === null) return true; // Default: show in discover (true)
      return saved === 'true';
    }
    return true;
  });

  // Check for system reduced motion preference
  const [reducedMotion, setReducedMotion] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
    return false;
  });

  // Listen for reduced motion changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);

    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const setPatternIntensity = (intensity: PatternIntensity) => {
    setPatternIntensityState(intensity);
    SafeStorage.setItem('afroyap_pattern_intensity', intensity);
  };

  const setReadingMode = (mode: ReadingMode) => {
    setReadingModeState(mode);
  };

  const setHighContrast = (enabled: boolean) => {
    setHighContrastState(enabled);
    SafeStorage.setItem('afroyap_high_contrast', String(enabled));
  };

  const setProfileVisibility = (visibility: 'public' | 'friends' | 'private') => {
    setProfileVisibilityState(visibility);
    SafeStorage.setItem('afroyap_profile_visibility', visibility);
  };

  const setAllowMessages = (setting: 'everyone' | 'friends' | 'none') => {
    setAllowMessagesState(setting);
    SafeStorage.setItem('afroyap_allow_messages', setting);
  };

  const setShareLocation = (enabled: boolean) => {
    setShareLocationState(enabled);
    SafeStorage.setItem('afroyap_share_location', String(enabled));
  };

  const setShowFriendList = (show: boolean) => {
    setShowFriendListState(show);
    SafeStorage.setItem('afroyap_show_friend_list', String(show));
  };

  const setFriendListVisibility = (visibility: 'everyone' | 'friends' | 'private') => {
    setFriendListVisibilityState(visibility);
    SafeStorage.setItem('afroyap_friend_list_visibility', visibility);
  };

  const setShowTribesList = (show: boolean) => {
    setShowTribesListState(show);
    SafeStorage.setItem('afroyap_show_tribes_list', String(show));
  };

  const setTribeListVisibility = (visibility: 'everyone' | 'friends' | 'private') => {
    setTribeListVisibilityState(visibility);
    SafeStorage.setItem('afroyap_tribe_list_visibility', visibility);
  };

  const setShowYaps = (show: boolean) => {
    setShowYapsState(show);
    SafeStorage.setItem('afroyap_show_yaps', String(show));
  };

  const setShowInDiscover = (show: boolean) => {
    setShowInDiscoverState(show);
    SafeStorage.setItem('afroyap_show_in_discover', String(show));
  };

  /**
   * Sync from Firestore: called by UserDataContext when the profile snapshot arrives.
   * Firestore values are authoritative and always override localStorage.
   */
  const syncFromProfile = (profile: {
    showFriendList?: boolean;
    friendListVisibility?: 'everyone' | 'friends' | 'private';
    showTribesList?: boolean;
    tribeListVisibility?: 'everyone' | 'friends' | 'private';
    showYaps?: boolean;
    showInDiscover?: boolean;
    allowMessages?: 'everyone' | 'friends' | 'none';
    notificationPreferences?: any;
  }) => {
    if (profile.allowMessages) {
      setAllowMessagesState(profile.allowMessages);
      SafeStorage.setItem('afroyap_allow_messages', profile.allowMessages);
    }
    if (profile.showFriendList !== undefined) {
      setShowFriendListState(profile.showFriendList);
      SafeStorage.setItem('afroyap_show_friend_list', String(profile.showFriendList));
    }
    if (profile.friendListVisibility) {
      setFriendListVisibilityState(profile.friendListVisibility);
      SafeStorage.setItem('afroyap_friend_list_visibility', profile.friendListVisibility);
    }
    if (profile.showTribesList !== undefined) {
      setShowTribesListState(profile.showTribesList);
      SafeStorage.setItem('afroyap_show_tribes_list', String(profile.showTribesList));
    }
    if (profile.tribeListVisibility) {
      setTribeListVisibilityState(profile.tribeListVisibility);
      SafeStorage.setItem('afroyap_tribe_list_visibility', profile.tribeListVisibility);
    }
    if (profile.showYaps !== undefined) {
      setShowYapsState(profile.showYaps);
      SafeStorage.setItem('afroyap_show_yaps', String(profile.showYaps));
    }
    if (profile.showInDiscover !== undefined) {
      setShowInDiscoverState(profile.showInDiscover);
      SafeStorage.setItem('afroyap_show_in_discover', String(profile.showInDiscover));
    }
    if (profile.notificationPreferences) {
      setNotificationPrefsState((prev: any) => ({ ...prev, ...profile.notificationPreferences }));
      SafeStorage.setItem('afroyap_notif_prefs', JSON.stringify({ ...notificationPrefs, ...profile.notificationPreferences }));
    }
  };

  const getPatternClass = () => {
    if (readingMode === 'reading') return '';
    if (patternIntensity === 'off') return '';
    if (patternIntensity === 'subtle') return 'african-pattern-bg-subtle';
    return 'african-pattern-bg';
  };

  return (
    <SettingsContext.Provider
      value={{
        patternIntensity,
        setPatternIntensity,
        readingMode,
        setReadingMode,
        reducedMotion,
        highContrast,
        setHighContrast,
        profileVisibility,
        setProfileVisibility,
        allowMessages,
        setAllowMessages,
        shareLocation,
        setShareLocation,
        showFriendList,
        setShowFriendList,
        friendListVisibility,
        setFriendListVisibility,
        showTribesList,
        setShowTribesList,
        tribeListVisibility,
        setTribeListVisibility,
        showYaps,
        setShowYaps,
        showInDiscover,
        setShowInDiscover,
        notificationPrefs,
        setNotificationPrefs,
        syncFromProfile,
        getPatternClass,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
